import { Injectable } from "@angular/core";
import * as Sentry from '@sentry/angular-ivy';
import { AppConfig } from '../../app/app.config';

@Injectable({
    providedIn: 'root',
})
export class SentryService {
    // Track whether Sentry has been initialized to prevent multiple initializations
    private isInitialized: boolean = false;

    public initializeSentry(): void {
        // Check if Sentry is already initialized
        if (this.isInitialized) {
            console.debug('Sentry is already initialized. Skipping re-initialization.');
            return;
        }

        // Check if Sentry is enabled in the configuration
        if (!AppConfig.settings || !AppConfig.settings.enableSentry) {
            console.debug('Sentry is disabled in this environment.');
            return;
        }

        // Get Sentry configuration from the loaded settings
        const sentryConfig = AppConfig.settings.sentry;

        if (!sentryConfig || !sentryConfig.dsn) {
            console.warn('Sentry configuration missing or incomplete.');
            return;
        }

        try {
            // Determine which product is being used based on the URL
            const currentUrl = window.location.href;
            const product = currentUrl.includes('musicnest.no') ? 'MusicNest' : 'MPO';
            const releaseVersion = '1.0';
            const releaseName = `${product}@${releaseVersion}`;

            // Initialize Sentry with the configuration from AppConfig
            Sentry.init({
                dsn: sentryConfig.dsn,
                environment: sentryConfig.environment,
                release: releaseName, // Set the release name based on the product
                sendDefaultPii: sentryConfig.defaultPii,
                integrations: [
                    Sentry.browserTracingIntegration({
                        tracingOrigins: sentryConfig.traceUrls || [],
                    }),
                    ...(sentryConfig.replaysIntegration ? [
                        Sentry.replayIntegration({
                            maskAllText: false,
                            blockAllMedia: false,
                            maskAllInputs: false,
                        })
                    ] : [])
                ],
                tracesSampleRate: 1.0,
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,
            });

            // Log which release was initialized
            console.debug(`Sentry initialized for ${product} release ${releaseVersion} in environment: ${sentryConfig.environment}`);

            // Mark as initialized
            this.isInitialized = true;
        } catch (error) {
            console.error('Error initializing Sentry:', error);
        }
    }

    /**
     * Capture an exception and send it to Sentry
     * @param error The error to capture
     * @param context Optional context information
     */
    public captureException(error: any, context?: any): void {
        if (this.isInitialized) {
            Sentry.captureException(error, { contexts: context });
        }
    }

    /**
     * Set user information for Sentry
     * @param user User information object
     */
    public setUser(user: { id?: string, email?: string, username?: string, board?: string }): void {
        if (this.isInitialized) {
            Sentry.setUser(user);
        }
    }

    /**
     * Clear user information
     */
    public clearUser(): void {
        if (this.isInitialized) {
            Sentry.setUser(null);
            Sentry.close();
        }
    }

    /**
     * Check if Sentry is already initialized
     */
    public isSentryInitialized(): boolean {
        return this.isInitialized;
    }
}
